import { call, put, select } from 'redux-saga/effects';
import { decode as atob } from 'base-64';

import { clearNativeSession, setNativeSession } from '../redux/Mobile/actions';
import makeSelectMobile from '../redux/Mobile/selectors';
import {
  AUTH_FULL,
  AUTH_INCOMPLETE,
  AUTH_MFA_SELECTION,
  AUTH_RECONSENT,
  AUTH_TOTP,
  COMPLETE_CLAIM_LEVEL,
  MFA_SELECTION_CLAIM_LEVEL,
  RECONSENT_CLAIM_LEVEL,
  TOTP_CLAIM_LEVEL,
} from '../redux/Main/constants';
import {
  AUTH_TOKEN_SESSION_KEY,
  SESSION_HEADERS_STORAGE_DICTIONARY,
} from './constants';
import {
  getCookie,
  getUnixTime,
  removeCookie,
  setCookie,
} from './globalHelpers';

/**
 * @description Retrieves the session object from the appropriate storage location,
 *  depending on native or web build
 * @returns {Object} session object
 */
export function* retrieveSession() {
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  let session = {};
  if (isNativeMobile) {
    session = yield select(makeSelectMobile('session'));
  } else {
    session = Object
      .keys(SESSION_HEADERS_STORAGE_DICTIONARY)
      .reduce((acc, sessionStorageKey) => {
        const value = getCookie(sessionStorageKey);
        return { ...acc, [sessionStorageKey]: value };
      }, {});
  }
  return session;
}

/**
 * @description Updates the session object in the appropriate location, based on build
 * @param {Object} session Object with updated session attribute key-value pairs.
 *  Can pass in a subset of the session object fields.
 * @returns {void}
 */
export function* setSession(session) {
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  /*
    Remove session properties that are empty strings, since if the server sets a header as
    an empty string, that indicates it should be reset/deleted (imitating cookie syntax/logic).
  */
  if (isNativeMobile) {
    const filteredSession = Object.entries(session)
      .filter(([, val]) => val !== '')
      .reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {});
    yield put(setNativeSession(filteredSession));
  } else {
    Object.entries(session)
      .forEach(([key, value]) => {
        if (value === '') {
          removeCookie(key);
        } else {
          setCookie(key, value);
        }
      });
  }
}

/**
 * @description Removes the session object in the appropriate location, based on build
 * @returns {void}
 */
export function* clearSession() {
  const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
  if (isNativeMobile) {
    yield put(clearNativeSession());
  } else {
    const session = yield call(retrieveSession);
    Object
      .keys(session)
      .forEach((key) => removeCookie(key));
  }
}

/**
 * @description Determines the auth status based on the data found in the user's session
 * cookie.
 *
 * @return {String} The current auth status
 *
 * AUTH_FULL: Fully authenticated. The user is logged in.
 * AUTH_TOTP: Valid username and password but requires TOTP
 * AUTH_RECONSENT: The user needs to reconsent to disclosures prior to logging in
 * AUTH_INCOMPLETE: The user is not authenticated/logged in.
 */
export function* getAuthStatus() {
  const { [AUTH_TOKEN_SESSION_KEY]: authToken } =
    yield call(retrieveSession);
  const { claimLevel, exp, isValidToken } = parseAuthToken(authToken);
  if (isValidToken && getUnixTime() < exp) {
    if (claimLevel === COMPLETE_CLAIM_LEVEL) return AUTH_FULL;
    if (claimLevel === MFA_SELECTION_CLAIM_LEVEL) return AUTH_MFA_SELECTION;
    if (claimLevel === TOTP_CLAIM_LEVEL) return AUTH_TOTP;
    if (claimLevel === RECONSENT_CLAIM_LEVEL) return AUTH_RECONSENT;
  }
  return AUTH_INCOMPLETE;
}

/**
 * @description Retrieves a property from the authToken object stored in session.
 * @param {string} prop Property to retrieve
 * @return {any} If the authToken exists and is valid, the value of the desired prop will be
 * returned. Else, `null` will be returned.
 */
export function* getAuthProp(prop) {
  const { [AUTH_TOKEN_SESSION_KEY]: authToken } = yield call(retrieveSession);
  const session = parseAuthToken(authToken);
  const { isValidToken } = session;
  if (isValidToken) return session[prop];
  return null;
}

/**
 * @description Parses the JWT authorization token to make the payload data accessible
 * @param {String} token Proxy server generated JWT Authorization token
 * @returns {Object} JWT authorization payload data
 */
function parseAuthToken(token = '') {
  const splitAuthToken = token.split('.');
  if (splitAuthToken.length === 3) {
    const [, payload] = splitAuthToken;
    const base64Payload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decodedPayload =
    decodeURIComponent(
      atob(base64Payload)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`).join(''),
    );
    return JSON.parse(decodedPayload);
  }
  return { isValidToken: false };
}
